import React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";
import { useOnboardingAcceptProfiles } from "@/views/Onboarding/hooks";
import { OnboardingProfilesHeader } from "@/views/Onboarding/ProfilesList/OnboardingProfilesHeader";
import { OnboardingProfilesRecord } from "@/views/Onboarding/ProfilesList/OnboardingProfilesRecord";

import "./_onboarding-profiles-list.scss";

interface IProfilesListModalProps {
  payload: IConsultantProfile;
  onClose: (shouldRefresh?: boolean) => void;
  open: boolean;
}

interface IModalProps extends IDialogModal {
  companyName: string;
  payload: IConsultantProfile;
}

const Modal: React.FC<IModalProps> = ({ payload, onClose }) => {
  const { t } = useTranslation("session");
  const { handleContinueClick } = useOnboardingAcceptProfiles(
    payload.sharedProfile ? payload.sharedProfile.company.id : undefined,
  );
  const handleContinue = async () => {
    await handleContinueClick(payload.id);
    onClose();
  };

  return (
    <StyledModal>
      <Flex direction="column" padding={["lg"]} innerMargin="lg" className="onboarding-profile-list">
        <Flex padding={[false]} justify="space-between" direction="row" align="center">
          <Header weight="bold">{t("ONBOARDING.PROFILES_LIST.MODAL.HEADER")}</Header>
          <CloseButton onClick={onClose} />
        </Flex>
        <Flex direction="column" padding={[false, false, "md", false]}>
          <OnboardingProfilesHeader />
          <OnboardingProfilesRecord profile={payload} />
        </Flex>
        <Flex justify="flex-end">
          <Button size="lg" onClick={handleContinue} className="continue-button">
            <Text>{t("ONBOARDING.PROFILES_LIST.LIST.BUTTON_CONTINUE")}</Text>
          </Button>
        </Flex>
      </Flex>
    </StyledModal>
  );
};

const ProfilesListModalComponent = inDialogModal(Modal);

export const ProfilesListModal: React.FC<IProfilesListModalProps> = React.memo(({ open, payload, onClose }) => {
  const companyName = (payload.sharedProfile && payload.sharedProfile.company.name) || "";

  if (open && companyName !== "") {
    return (
      <ProfilesListModalComponent companyName={companyName} open={open} payload={payload} onClose={onClose} size="md" />
    );
  }

  return null;
});
